import { useState, useEffect, useCallback, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import { superAdminApi } from '../services/api';
import { SuperAdminNav } from './SuperAdminConsole';
import Chip from '../components/Chip';

const PAGE_SIZE = 15;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// "2026-08-20" -> "20-Aug-2026" (API expects dd-MMM-yyyy)
function isoToApi(iso) {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

// "2026-08-20" -> "20-Aug-2026"
function fmtDisplay(iso) {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

function rangeForPreset(preset) {
  const end = new Date();
  let start = new Date();
  if (preset === 'today') { /* start = end */ }
  else if (preset === '7') start.setDate(start.getDate() - 6);
  else if (preset === '30') start.setDate(start.getDate() - 29);
  else if (preset === '90') start.setDate(start.getDate() - 89);
  else if (preset === 'month') start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { from: toIso(start), to: toIso(end) };
}

function inr(n) { return '₹' + Number(n || 0).toLocaleString('en-IN'); }

// Text input showing dd-MMM-yyyy with a bootstrap-datepicker calendar (value/onChange are ISO yyyy-mm-dd)
function DateField({ value, onChange, placeholder = 'dd-MMM-yyyy', max, min }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      orientation: 'bottom auto',
      endDate: max ? new Date(`${max}T00:00:00`) : undefined,
      startDate: min ? new Date(`${min}T00:00:00`) : undefined,
    });
    $input.on('changeDate', e => onChange(toIso(e.date)));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker('setEndDate', max ? new Date(`${max}T00:00:00`) : null);
    $input.datepicker('setStartDate', min ? new Date(`${min}T00:00:00`) : null);
  }, [max, min]);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker('update', value ? new Date(`${value}T00:00:00`) : '');
  }, [value]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        ref={ref}
        className="inp"
        type="text"
        readOnly
        defaultValue={fmtDisplay(value)}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: 28, width: 150 }}
      />
      <span style={{ position: 'absolute', right: 8, cursor: 'pointer', color: 'var(--g400)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

// Windowed page list: 1 2 3 … N when near the start, 1 … n-1 n n+1 … N in the middle.
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current]);
  if (current <= 3) { set.add(2); set.add(3); }
  else if (current >= total - 2) { set.add(total - 1); set.add(total - 2); }
  else { set.add(current - 1); set.add(current + 1); }
  const sorted = [...set].sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push(`e${p}`);
    items.push(p);
  });
  return items;
}

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pag">
      <button className="pgbtn" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}>&lsaquo;</button>
      {pageWindow(page, totalPages).map(item => (
        typeof item === 'number'
          ? <button key={item} className={`pgbtn ${item === page ? 'on' : ''}`} onClick={() => onChange(item)}>{item}</button>
          : <span key={item} className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>
      ))}
      <button className="pgbtn" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}>&rsaquo;</button>
    </div>
  );
}

export default function AdminPaymentTransactions({ onNavigate }) {
  const [preset, setPreset] = useState('30');
  const [range, setRange] = useState(() => rangeForPreset('30'));
  const [fromInput, setFromInput] = useState(range.from);
  const [toInput, setToInput] = useState(range.to);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);

  const fetchData = useCallback((from, to, pageNo) => {
    setLoading(true);
    Promise.all([
      superAdminApi.getPaymentTransactions(isoToApi(from), isoToApi(to), pageNo, PAGE_SIZE).catch(() => null),
      superAdminApi.getWalletDashboard(isoToApi(from), isoToApi(to), 1).catch(() => null),
    ]).then(([txnRes, dashRes]) => {
      setItems(txnRes?.data?.items || []);
      setTotalCount(txnRes?.data?.totalcount || 0);
      setSummary(dashRes?.data?.summary || null);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(range.from, range.to, page); }, []); // eslint-disable-line

  function selectPreset(key) {
    setPreset(key);
    setPage(1);
    if (key === 'custom') return;
    const r = rangeForPreset(key);
    setRange(r);
    setFromInput(r.from);
    setToInput(r.to);
    fetchData(r.from, r.to, 1);
  }

  function applyCustom() {
    if (!fromInput || !toInput) return;
    setPreset('custom');
    setPage(1);
    setRange({ from: fromInput, to: toInput });
    fetchData(fromInput, toInput, 1);
  }

  function goToPage(p) {
    setPage(p);
    fetchData(range.from, range.to, p);
  }

  const periodLabel = `${fmtDisplay(range.from)} – ${fmtDisplay(range.to)}`;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showFrom = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(page * PAGE_SIZE, totalCount);
  const totalCredited = summary?.total_credited || 0;
  const creditedCount = summary?.clients_credited_count || 0;

  return (
    <>
      <SuperAdminNav onNavigate={onNavigate} onChangePassword={() => {}} currentPage="admin-payment-transactions" />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Payment Transactions</div>
            <div className="psub">Wallet credits added by clients, across the platform</div>
          </div>
        </div>

        {/* Date range filter */}
        <div className="card mb20">
          <div className="dfilter">
            <div className="drow">
              <div className="flbl">📅 Date range</div>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`dpill ${preset === p.key ? 'on' : ''}`}
                  onClick={() => selectPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
              <DateField
                value={fromInput}
                max={toInput}
                onChange={v => { setFromInput(v); setPreset('custom'); }}
              />
              <span className="ddash">to</span>
              <DateField
                value={toInput}
                min={fromInput}
                onChange={v => { setToInput(v); setPreset('custom'); }}
              />
              <button className="btn bpri bsm" onClick={applyCustom}>Apply</button>
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="gr3 mb20">
          <div className="sc">
            <div className="si si-gr">₹</div>
            <div>
              <div className="slbl">Total Credited</div>
              <div className="sval" style={{ color: 'var(--gr)' }}>{loading ? '…' : inr(totalCredited)}</div>
              <div className="snote">{periodLabel}</div>
            </div>
          </div>
          <div className="sc">
            <div className="si si-bl">🏢</div>
            <div>
              <div className="slbl">Clients Credited</div>
              <div className="sval">{loading ? '…' : creditedCount}</div>
              <div className="snote">Unique clients in period</div>
            </div>
          </div>
          <div className="sc">
            <div className="si si-or">🧾</div>
            <div>
              <div className="slbl">Total Transactions</div>
              <div className="sval">{loading ? '…' : totalCount}</div>
              <div className="snote">Credit entries in period</div>
            </div>
          </div>
        </div>

        {/* Transaction table */}
        <div className="card">
          <div className="chd">
            <div><div className="ctitle">Credit Transactions</div><div className="csub">{periodLabel}</div></div>
          </div>
          <div className="twrap">
            <table>
              <thead>
                <tr><th>Date &amp; Time</th><th>Client</th><th>Type</th><th>Amount Credited</th><th>Reference</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>No credit transactions in this period</td></tr>
                )}
                {!loading && items.map((t, i) => (
                  <tr key={t.trans_id ?? i}>
                    <td className="tdsm">{t.created_on || '—'}</td>
                    <td>
                      <div className="td1">{t.client_name || '—'}</div>
                      {t.remarks && <div className="tdm">{t.remarks}</div>}
                    </td>
                    <td><Chip label={t.trans_type || '—'} className="cg" /></td>
                    <td style={{ fontWeight: 700, color: 'var(--gr)' }}>+{inr(t.amount)}</td>
                    <td className="tdsm">{t.reference_type ? `${t.reference_type} ${t.reference_id || ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex jb aic mt16">
            <div className="ts tg">Showing {showFrom}–{showTo} of {totalCount} transaction{totalCount !== 1 ? 's' : ''}</div>
            <Pager page={page} totalPages={totalPages} onChange={goToPage} />
          </div>
        </div>
      </div>
    </>
  );
}
